// Typed REST client for forge-admin. Matches the axum routes exactly.

export type EntityStatus = 'draft' | 'published';

export interface ConfigRef {
  target_package: string;
  target_entity_type: string;
  target_key: string;
  reference_type: string;
  required: boolean;
}

export interface ConfigEntityRow {
  id: string;
  game_id: string;
  package: string;
  entity_type: string;
  entity_key: string;
  status: EntityStatus;
  data: unknown;
  version: number;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  created_by: string | null;
}

export interface ConfigHistoryRow {
  id: string;
  entity_id: string;
  version: number;
  data: unknown;
  change_summary: string;
  changed_by: string | null;
  changed_at: string;
}

export interface ConfigSnapshot {
  id: string;
  game_id: string;
  name: string;
  created_at: number; // ms since epoch
  created_by: string | null;
  notes: string;
  is_current: boolean;
  changed_packages: string[];
}

export interface DiffChange {
  kind: 'added' | 'removed' | 'modified';
  data?: unknown;
  before?: unknown;
  after?: unknown;
}

export interface ConfigDiff {
  package: string;
  entity_type: string;
  entity_key: string;
  change: DiffChange;
}

export interface AuditEntry {
  game_id: string;
  package: string;
  event_type: string;
  scope: unknown;
  at: number;
  data: unknown;
}

export interface ReferenceGraph {
  outgoing: ConfigRef[];
  incoming: ConfigRef[];
}

export interface Module {
  id: string;
  display_name: string;
  icon: string;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = localStorage.getItem('forge.token');
  const headers: Record<string, string> = {};
  if (body) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(path, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore JSON parse failure
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const tenant = (gameId: string, suffix: string) =>
  `/api/v1/games/${gameId}${suffix}`;

export interface AuthResponse {
  token?: string;
  user?: { id: string; email: string };
  challenge?: {
    challenge_id: string;
    kind: 'signup_verification' | 'login_otp';
    email: string;
    expires_at: string;
  };
}

export interface MeResponse {
  user: { id: string; email: string; games: GameInfoAuth[] };
}

export interface GameInfoAuth {
  game_id: string;
  name: string;
  enabled_packages: string[];
}

export interface GameCreated {
  game_id: string;
  name: string;
  enabled_packages: string[];
}

export type PlayerStatus = 'active' | 'disabled' | 'deleted';

export interface PlayerProfile {
  game_id: string;
  player_id: string;
  realm_id: string;
  external_user_id: string | null;
  display_name: string;
  status: PlayerStatus;
  metadata: unknown;
  created_at: string;
  updated_at: string;
}

export interface PlayerCharacter {
  game_id: string;
  player_id: string;
  character_id: string;
  realm_id: string;
  display_name: string;
  archetype_ref: string | null;
  is_primary: boolean;
  metadata: unknown;
  created_at: string;
  updated_at: string;
}

export interface PlayerHero {
  game_id: string;
  player_id: string;
  hero_instance_id: string;
  hero_ref: string;
  character_id: string | null;
  level: number;
  rank: number;
  active: boolean;
  metadata: unknown;
  created_at: string;
  updated_at: string;
}

export interface ApiKeyRecord {
  id: string;
  game_id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  owner_email: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface CreatedApiKey {
  key: ApiKeyRecord;
  token: string;
}

export interface ManagementKeyRecord {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  owner_email: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface CreatedManagementKey {
  key: ManagementKeyRecord;
  token: string;
}

export const api = {
  // Auth endpoints
  signup: (email: string, password: string) =>
    request<AuthResponse>('POST', '/api/v1/auth/signup', { email, password }),

  login: (email: string, password: string) =>
    request<AuthResponse>('POST', '/api/v1/auth/login', { email, password }),

  verifySignup: (challengeId: string, email: string, code: string) =>
    request<AuthResponse>('POST', '/api/v1/auth/verify-signup', {
      challenge_id: challengeId,
      email,
      code,
    }),

  verifyLogin: (challengeId: string, email: string, code: string) =>
    request<AuthResponse>('POST', '/api/v1/auth/verify-login', {
      challenge_id: challengeId,
      email,
      code,
    }),

  me: () => request<MeResponse>('GET', '/api/v1/auth/me'),

  createGame: (name: string, packages: string[]) =>
    request<GameCreated>('POST', '/api/v1/games/create', { name, enabled_packages: packages }),

  updateGame: (gameId: string, body: {
    name?: string;
    enabled_packages?: string[];
    realm_ids?: string[];
  }) => request<{
    game_id: string;
    name: string;
    enabled_packages: string[];
    realm_ids: string[];
    owner_email: string;
  }>('PATCH', `/api/v1/games/${gameId}`, body),

  listPlayers: (gameId: string) =>
    request<{ players: PlayerProfile[] }>('GET', tenant(gameId, '/players')),

  createPlayer: (gameId: string, body: {
    display_name: string;
    realm_id?: string;
    external_user_id?: string;
    metadata?: unknown;
  }) => request<PlayerProfile>('POST', tenant(gameId, '/players'), body),

  setPlayerStatus: (gameId: string, playerId: string, status: PlayerStatus) =>
    request<PlayerProfile>('POST', tenant(gameId, `/players/${playerId}/status`), { status }),

  listCharacters: (gameId: string, playerId: string) =>
    request<{ characters: PlayerCharacter[] }>(
      'GET',
      tenant(gameId, `/players/${playerId}/characters`),
    ),

  createCharacter: (gameId: string, playerId: string, body: {
    display_name: string;
    character_id?: string;
    realm_id?: string;
    archetype_ref?: string;
    is_primary?: boolean;
    metadata?: unknown;
  }) => request<PlayerCharacter>('POST', tenant(gameId, `/players/${playerId}/characters`), body),

  listHeroes: (gameId: string, playerId: string) =>
    request<{ heroes: PlayerHero[] }>('GET', tenant(gameId, `/players/${playerId}/heroes`)),

  unlockHero: (gameId: string, playerId: string, body: {
    hero_ref: string;
    hero_instance_id?: string;
    character_id?: string;
    level?: number;
    rank?: number;
    metadata?: unknown;
  }) => request<PlayerHero>('POST', tenant(gameId, `/players/${playerId}/heroes`), body),

  listApiKeys: (gameId: string) =>
    request<{ keys: ApiKeyRecord[] }>('GET', tenant(gameId, '/api-keys')),

  createApiKey: (gameId: string, body: { name: string; scopes?: string[] }) =>
    request<CreatedApiKey>('POST', tenant(gameId, '/api-keys'), body),

  revokeApiKey: (gameId: string, keyId: string) =>
    request<ApiKeyRecord>('POST', tenant(gameId, `/api-keys/${keyId}/revoke`)),

  listManagementKeys: () =>
    request<{ keys: ManagementKeyRecord[] }>('GET', '/api/v1/auth/management-keys'),

  createManagementKey: (body: { name: string; scopes?: string[] }) =>
    request<CreatedManagementKey>('POST', '/api/v1/auth/management-keys', body),

  revokeManagementKey: (keyId: string) =>
    request<ManagementKeyRecord>('POST', `/api/v1/auth/management-keys/${keyId}/revoke`),

  health: () => request<{ status: string; service: string; version: string }>(
    'GET', '/api/v1/health'
  ),

  modules: () => request<Module[]>('GET', '/api/v1/modules'),

  listSchemas: () =>
    request<{ package: string; entity_type: string }[]>('GET', '/api/v1/schemas'),

  getSchema: (pkg: string, entityType: string) =>
    request<Record<string, unknown>>('GET', `/api/v1/schemas/${pkg}/${entityType}`),

  listEntities: (gameId: string, filters: Record<string, string> = {}) => {
    const q = new URLSearchParams(filters).toString();
    const suffix = q ? `?${q}` : '';
    return request<ConfigEntityRow[]>('GET', tenant(gameId, `/entities${suffix}`));
  },

  createEntity: (gameId: string, body: {
    package: string;
    entity_type: string;
    entity_key: string;
    data: unknown;
    references?: ConfigRef[];
    user_id?: string;
  }) => request<ConfigEntityRow>('POST', tenant(gameId, '/entities'), body),

  getEntity: (gameId: string, id: string) =>
    request<ConfigEntityRow>('GET', tenant(gameId, `/entities/${id}`)),

  updateEntity: (gameId: string, id: string, body: {
    data: unknown;
    references?: ConfigRef[];
    user_id?: string;
  }) => request<ConfigEntityRow>('PUT', tenant(gameId, `/entities/${id}`), body),

  deleteEntity: (gameId: string, id: string) =>
    request<{ deleted: string }>('DELETE', tenant(gameId, `/entities/${id}`)),

  getHistory: (gameId: string, id: string) =>
    request<ConfigHistoryRow[]>('GET', tenant(gameId, `/entities/${id}/history`)),

  getReferences: (gameId: string, id: string) =>
    request<ReferenceGraph>('GET', tenant(gameId, `/entities/${id}/references`)),

  listDrafts: (gameId: string) =>
    request<ConfigEntityRow[]>('GET', tenant(gameId, '/drafts')),

  saveDraft: (gameId: string, body: {
    package: string;
    entity_type: string;
    entity_key: string;
    data: unknown;
    user_id: string;
  }) => request<unknown>('POST', tenant(gameId, '/drafts/save'), body),

  discardDrafts: (gameId: string) =>
    request<{ discarded: number }>('DELETE', tenant(gameId, '/drafts')),

  validateDrafts: (gameId: string) =>
    request<{ problems: unknown[]; has_fatal: boolean }>(
      'POST', tenant(gameId, '/drafts/validate')
    ),

  preview: (gameId: string) =>
    request<unknown>('GET', tenant(gameId, '/preview')),

  publish: (gameId: string, body: { user_id: string; notes: string }) =>
    request<ConfigSnapshot>('POST', tenant(gameId, '/publish'), body),

  rollback: (gameId: string, snapshotId: string, userId: string) =>
    request<ConfigSnapshot>('POST', tenant(gameId, `/rollback/${snapshotId}`), {
      user_id: userId,
    }),

  listSnapshots: (gameId: string) =>
    request<ConfigSnapshot[]>('GET', tenant(gameId, '/snapshots')),

  getSnapshot: (gameId: string, id: string) =>
    request<{ snapshot: ConfigSnapshot; pins: unknown[] }>(
      'GET', tenant(gameId, `/snapshots/${id}`)
    ),

  diffSnapshots: (gameId: string, a: string, b: string) =>
    request<ConfigDiff[]>('GET', tenant(gameId, `/snapshots/${a}/diff/${b}`)),

  auditLog: (gameId: string, filters: Record<string, string> = {}) => {
    const q = new URLSearchParams(filters).toString();
    const suffix = q ? `?${q}` : '';
    return request<AuditEntry[]>('GET', tenant(gameId, `/audit${suffix}`));
  },

  exportYaml: (gameId: string) =>
    request<{ files: Record<string, string> }>(
      'POST', tenant(gameId, '/yaml/export')
    ),

  importYaml: (gameId: string, body: {
    user_id: string;
    files: Record<string, string>;
  }) => request<unknown[]>('POST', tenant(gameId, '/yaml/import'), body),

  // ---- resource module ----
  resourceWallet: (gameId: string, scopeKind: string, scopeId: string) =>
    request<{
      scope: unknown;
      state: { balances: Record<string, { value: number; decimal_places: number }> };
    }>('GET', tenant(gameId, `/modules/resource/wallets/${scopeKind}/${scopeId}`)),

  resourceGrant: (
    gameId: string,
    scopeKind: string,
    scopeId: string,
    body: { resource_id: string; amount: number },
  ) => request<{ new_state: unknown; changes: number }>(
    'POST',
    tenant(gameId, `/modules/resource/wallets/${scopeKind}/${scopeId}/grant`),
    body,
  ),

  resourceCost: (
    gameId: string,
    scopeKind: string,
    scopeId: string,
    body: { resource_id: string; amount: number },
  ) => request<{ new_state: unknown; changes: number }>(
    'POST',
    tenant(gameId, `/modules/resource/wallets/${scopeKind}/${scopeId}/cost`),
    body,
  ),

  resourceRegen: (
    gameId: string,
    scopeKind: string,
    scopeId: string,
    body: { now_ms?: number },
  ) => request<{ new_state: unknown; changes: number }>(
    'POST',
    tenant(gameId, `/modules/resource/wallets/${scopeKind}/${scopeId}/regen`),
    body,
  ),

  // ---- realms ----
  listRealms: (gameId: string) =>
    request<RealmDefinition[]>('GET', tenant(gameId, '/realms')),

  getRealm: (gameId: string, realmId: string) =>
    request<RealmDefinition>('GET', tenant(gameId, `/realms/${realmId}`)),

  createRealm: (gameId: string, body: RealmDefinition) =>
    request<RealmDefinition>('POST', tenant(gameId, '/realms'), body),

  updateRealm: (gameId: string, realmId: string, body: RealmDefinition) =>
    request<RealmDefinition>('PUT', tenant(gameId, `/realms/${realmId}`), body),

  deleteRealm: (gameId: string, realmId: string) =>
    request<{ deleted: string }>('DELETE', tenant(gameId, `/realms/${realmId}`)),

  setRealmStatus: (gameId: string, realmId: string, status: string) =>
    request<RealmDefinition>('POST', tenant(gameId, `/realms/${realmId}/status`), { status }),

  mergeAnalyze: (gameId: string, sourceIds: string[], targetId: string) =>
    request<MergeAnalysis>('POST', tenant(gameId, '/realms/merge/analyze'), {
      source_realm_ids: sourceIds,
      target_realm_id: targetId,
    }),

  mergeExecute: (gameId: string, sourceIds: string[], targetId: string) =>
    request<unknown>('POST', tenant(gameId, '/realms/merge/execute'), {
      source_realm_ids: sourceIds,
      target_realm_id: targetId,
    }),
};

export interface MergeAnalysis {
  sources: string[];
  target: string;
  conflicts: { kind: unknown; description: string; source_realm: string }[];
  can_proceed: boolean;
}

export interface RealmDefinition {
  realm_id: string;
  display_name: string;
  status: string;
  max_players: number;
  created_at: number;
  merge_history: unknown[];
  tags: string[];
  config_overrides: Record<string, unknown>;
}
